/**
 * Inline remediation controls for missing criterion rationale warnings.
 */

import { useState } from "react";
import type { ProtocolCriterion } from "../../api/clinicalTrialsApiTypes";

type CriterionRationaleActionsProps = {
  criteria: ProtocolCriterion[];
  busy: boolean;
  onSave: (criterion: ProtocolCriterion, rationale: string) => Promise<void>;
};

/**
 * Render affected criteria with an inline rationale editor.
 *
 * @param props - Affected criteria, busy state, and save callback
 * @returns Expandable remediation list
 */
export function CriterionRationaleActions({
  criteria,
  busy,
  onSave,
}: CriterionRationaleActionsProps) {
  const [expanded, setExpanded] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rationale, setRationale] = useState("");

  if (criteria.length === 0) return null;

  return (
    <div className="mt-3">
      <button
        type="button"
        className="rounded border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900"
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded
          ? "Hide affected criteria"
          : `Review ${criteria.length} criteria`}
      </button>
      {expanded ? (
        <div className="mt-3 max-h-96 space-y-2 overflow-auto pr-1">
          {criteria.map((criterion) => {
            const editing = editingId === criterion.criterion_id;
            return (
              <div
                key={criterion.criterion_id}
                className="rounded border border-amber-200 bg-white p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold text-slate-900">
                      {criterion.criterion_no} · {criterion.criterion_type}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600">
                      {criterion.criterion_text_verbatim}
                    </p>
                  </div>
                  {!editing ? (
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-sky-700 hover:underline"
                      onClick={() => {
                        setEditingId(criterion.criterion_id);
                        setRationale(criterion.rationale ?? "");
                      }}
                    >
                      Add rationale
                    </button>
                  ) : null}
                </div>
                {editing ? (
                  <div className="mt-3">
                    <textarea
                      className="min-h-20 w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
                      placeholder="Explain the clinical or operational rationale for this criterion…"
                      value={rationale}
                      onChange={(event) => setRationale(event.target.value)}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        disabled={busy || !rationale.trim()}
                        className="rounded bg-slate-900 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                        onClick={() =>
                          void onSave(criterion, rationale.trim())
                            .then(() => {
                              setEditingId(null);
                              setRationale("");
                            })
                            .catch(() => undefined)
                        }
                      >
                        Save rationale
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        className="rounded border border-slate-300 px-3 py-1.5 text-xs"
                        onClick={() => {
                          setEditingId(null);
                          setRationale("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
